import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { usePopupRef } from '@framework/hooks';
import { useTranslation } from 'react-i18next';
import { ForeignObjectAutosize } from '@view-model/models/common/components/ForeignObjectAutosize';
import { ModelCommentContentItemContainer } from './ModelCommentContentItemContainer';
import { ModelCommentReplyFormContainer } from './ModelCommentReplyFormContainer';
import { ModelCommentItem } from '../ModelCommentItem';
import { useNow } from '@framework/hooks/useNow';
import { ModelCommentReply } from './ModelCommentReply';
import { ModelCommentMenuList } from '../ModelCommentMenuList';
import { CommentConstants } from '../../constants';
import { ModelCommentThreadHeader } from './ModelCommentThreadHeader';
import type { ModelCommentThread } from '../../domain';
import { ModelCommentThreadOperation } from '../../adapter';
import { useCopyCommentThreadUrl } from '../../adapter/useCopyCommentThreadUrl';
import { ModelCommentDeleteConfirmModal } from '../ModelCommentDeleteConfirmModal';
import { Size } from '@view-model/models/common/basic';
import { CommentPositionMap } from '../../positions';
import { useTextSelectable } from '@view-model/models/common/hooks';
import { useCommandManager } from '@model-framework/command';
import type { ModelCommentId, ModelCommentThreadId, ModelId, ViewModelId } from 'balus-common';
import type { UserPublicProfile } from '@user/PublicProfile';

type Props = {
  viewModelId: ViewModelId;
  modelId: ModelId;
  commentThread: ModelCommentThread;
  readonly: boolean;
  currentUserProfile: UserPublicProfile;
  onSizeChange(id: ModelCommentThreadId, newSize: Size): void;
  onClose(): void;
};

export const ModelCommentThreadContent = memo(
  ({ viewModelId, modelId, commentThread, readonly, currentUserProfile, onSizeChange, onClose }: Props) => {
    const threadId = commentThread.id;
    const isResolved = commentThread.isResolved();
    const primaryComment = commentThread.getPrimaryComment();
    const replyComments = commentThread.getReplyComments();

    const commandManager = useCommandManager();

    const threadOperation = useMemo(
      () => new ModelCommentThreadOperation(viewModelId, modelId, threadId, commandManager),
      [commandManager, modelId, threadId, viewModelId]
    );

    const [containerSize, setContainerSize] = useState<Size>(new Size(0, 0));
    const [sizes, setSizes] = useState<Record<ModelCommentId, Size>>({});
    const [editingCommentId, setEditingCommentId] = useState<ModelCommentId | null>(null);
    const positionMap = new CommentPositionMap(commentThread, sizes);

    const ref = useRef(null);
    // マウスイベントが伝搬することでテキスト操作（選択、リンククリック）ができず、ドラッグによりキャンバス自体が移動してしまうのを止める
    useTextSelectable(ref);

    const now = useNow();
    const { width, headerBarHeight } = CommentConstants.UISetting;

    const { t } = useTranslation();
    const {
      isOpen: isEllipsisMenuOpen,
      popupRef: ellipsisMenuRef,
      handleOpen: handleEllipsisMenuOpen,
      handleClose: handleEllipsisMenuClose,
    } = usePopupRef<HTMLDivElement>(false);

    const handleShareThread = useCopyCommentThreadUrl(threadId, modelId);

    const handleShareThreadClick = useCallback(() => {
      handleEllipsisMenuClose();
      handleShareThread();
    }, [handleEllipsisMenuClose, handleShareThread]);

    const [isDeleteThreadConfirmOpen, setIsDeleteThreadConfirmOpen] = useState(false);

    const handleDeleteThreadClick = useCallback(() => {
      handleEllipsisMenuClose();
      setIsDeleteThreadConfirmOpen(true);
    }, [handleEllipsisMenuClose]);

    const handleDeleteThreadConfirm = useCallback(() => {
      setIsDeleteThreadConfirmOpen(false);
      threadOperation.deleteThread().then();
    }, [threadOperation]);

    const handleDeleteThreadConfirmClose = useCallback(() => {
      setIsDeleteThreadConfirmOpen(false);
    }, []);

    const handleChangeContainerSize = useCallback(
      (size: Size) => {
        // foreignObject の大きさに対してヘッダー高さを加えた値が、このコメントの表示サイズ
        const newSize = size.addHeight(headerBarHeight);
        setContainerSize(newSize);
        onSizeChange(threadId, newSize);
      },
      [headerBarHeight, onSizeChange, threadId]
    );

    const handleChangeSize = useCallback((id: ModelCommentId, newSize: Size) => {
      setSizes((sizes) => ({ ...sizes, [id]: newSize }));
    }, []);

    const handleEditStart = useCallback((id: ModelCommentId) => {
      setEditingCommentId(id);
    }, []);

    const handleEditCancel = useCallback(() => {
      setEditingCommentId(null);
    }, []);

    const handleEditSubmit = useCallback(() => {
      setEditingCommentId(null);
    }, []);

    const handleReplyCommentSubmit = useCallback(
      (body: string) => {
        threadOperation.addReplyComment(body, currentUserProfile.id).then();
      },
      [currentUserProfile, threadOperation]
    );

    return (
      <>
        {/* rectで背景を作成 */}
        <rect
          width={containerSize.width}
          height={containerSize.height}
          fill="white"
          stroke="#d1d5db"
          strokeWidth="4"
          rx="16"
          ry="16"
        />

        {/* ヘッダー */}
        <ModelCommentThreadHeader
          width={width}
          height={headerBarHeight}
          isResolved={isResolved}
          threadOperation={threadOperation}
          currentUserProfile={currentUserProfile}
          onClose={onClose}
          onEllipsisClick={handleEllipsisMenuOpen}
        />

        {/* ドラッグハンドル部分の高さ分だけ y軸方向にオフセットする */}
        <g ref={ref} transform={`translate(0, ${headerBarHeight})`}>
          <ForeignObjectAutosize onSizeChange={handleChangeContainerSize}>
            <div
              className="rounded-b-2xl border-x-6 border-b-6 border-t-0 border-white bg-white pb-3 text-3xl"
              style={{ width: width }}
            >
              {/* スレッドの元コメント */}
              <ModelCommentContentItemContainer id={primaryComment.id} width={width} onSizeChange={handleChangeSize}>
                <ModelCommentItem
                  isEditing={primaryComment.id === editingCommentId}
                  comment={primaryComment}
                  now={now}
                  threadOperation={threadOperation}
                  onCancel={handleEditCancel}
                  onSubmit={handleEditSubmit}
                />
              </ModelCommentContentItemContainer>

              {/* 返信コメントを順に列挙 */}
              {replyComments.map((replyComment) => (
                <ModelCommentContentItemContainer
                  key={replyComment.id}
                  id={replyComment.id}
                  width={width}
                  onSizeChange={handleChangeSize}
                >
                  <ModelCommentItem
                    isEditing={replyComment.id === editingCommentId}
                    comment={replyComment}
                    now={now}
                    threadOperation={threadOperation}
                    onCancel={handleEditCancel}
                    onSubmit={handleEditSubmit}
                  />
                </ModelCommentContentItemContainer>
              ))}

              {/* 返信コメントの投稿フォーム (readonly の場合には非表示) */}
              {!readonly && (
                <ModelCommentReplyFormContainer>
                  <ModelCommentReply onReplyCommentSubmit={handleReplyCommentSubmit} />
                </ModelCommentReplyFormContainer>
              )}
            </div>
          </ForeignObjectAutosize>

          {/*
                    コメントに対する操作メニューを表示 (readonly の場合には非表示)

                    foreignObject の中で relative, absolute 等の相対位置指定を利用すると、Safari で表示崩れが発生する。
                    この表示崩れを回避するために、メニュー項目ごとに独立した foreignObject を用意して transform する形で実現する。
                  */}
          {!readonly && (
            <ModelCommentMenuList
              commentThread={commentThread}
              positionMap={positionMap}
              threadOperation={threadOperation}
              onEditStart={handleEditStart}
            />
          )}
        </g>

        {/* スレッド操作メニュー: SVG の最後に描画することで他要素より前面に表示される */}
        {isEllipsisMenuOpen && (
          <foreignObject x={width - 320} y={headerBarHeight} width={320} height={100} overflow="visible">
            <div
              ref={ellipsisMenuRef}
              role="menu"
              className="flex flex-col gap-2 rounded-xl bg-gray-700 p-2 text-3xl"
              style={{ boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.10), 0 8px 12px 0 rgba(0, 0, 0, 0.10)' }}
            >
              <button
                type="button"
                role="menuitem"
                className="rounded-xl px-8 py-2 text-3xl text-white hover:bg-brand-hover"
                onClick={handleShareThreadClick}
              >
                {t('canvas.comment.menu.shareThread')}
              </button>
              {primaryComment.userId === currentUserProfile.id && (
                <button
                  type="button"
                  role="menuitem"
                  className="rounded-xl px-8 py-2 text-3xl text-white hover:bg-brand-hover"
                  onClick={handleDeleteThreadClick}
                >
                  {t('canvas.comment.menu.deleteThread')}
                </button>
              )}
            </div>
          </foreignObject>
        )}
        <ModelCommentDeleteConfirmModal
          isOpen={isDeleteThreadConfirmOpen}
          type="thread"
          onConfirm={handleDeleteThreadConfirm}
          onClose={handleDeleteThreadConfirmClose}
        />
      </>
    );
  }
);
ModelCommentThreadContent.displayName = 'ModelCommentThreadContent';
